import { CHANNELS } from "../../shared/channels";
import { LIMITS, MAX_PATTERNS, ROW_CHOICES, SNES_LIMITS, TrackerMode, TrackerSong, TrackerSummary, analyse } from "../../shared/tracker";
import { Step } from "../../shared/tracker_render";
import { call, listen } from "../bridge";
import { byId, flashToast } from "../dom";
import { t } from "../i18n";
import { isViewActive, onViewHidden, onViewShown } from "../navigation";
import { isPlaying, playSong, stopSong } from "../tracker_player";
import { Scope, canRedo, canUndo, closeSong, clampToMode, edit, flushSave, loadSong, onSong, redo, state, undo } from "../tracker_store";
import { handleKey, renderNotes, setupNotes, showStep, closeSheet } from "./tracker_notes";
import { renderExport } from "./tracker_export";
import { earOn, renderSounds, setupSounds } from "./tracker_sounds";
import { iconSvg } from "../icons";

// the tracker: write music for a snes game and export it as an impulse tracker file. the shelf lists a person's songs,
// the editor has four tabs (notes, song, sounds, export) under a limits bar that is always in sight. SNES safe mode locks
// the song to what a real console can play, free mode only warns. the other files hold the pieces: tracker_store (the song
// and its undo), tracker_player (audio), tracker_notes, tracker_sounds, tracker_export

type Tab = "notes" | "song" | "sounds" | "export";
const TABS: Tab[] = ["notes", "song", "sounds", "export"];

let shelf: TrackerSummary[] = [];
let tab: Tab = "notes";
let orderAt = 0;

const editor = () => byId("tk-editor");
const kb = (bytes: number): string => (bytes / 1000).toFixed(1);

function say(text: string): void {
  byId("tk-live").textContent = text;
}

// --- the shelf

function shelfCard(item: TrackerSummary): HTMLElement {
  const card = document.createElement("button");
  card.type = "button";
  card.className = "tile tl-card";
  const media = document.createElement("span");
  media.className = "tile-media";
  media.innerHTML = iconSvg("tracker", 40);
  const title = document.createElement("span");
  title.className = "tile-title";
  title.textContent = item.title;
  const meta = document.createElement("span");
  meta.className = "tile-meta";
  meta.textContent = `${item.mode === "snes" ? t("SNES safe") : t("Free")} · ${t("{n} voices", { n: item.voices })} · ${t("{kb} KB", { kb: kb(item.bytes) })}`;
  const meter = document.createElement("span");
  const fill = item.bytes / SNES_LIMITS.bytes;
  meter.className = "tk-card-meter" + (fill > 1 ? " bad" : fill > 0.8 ? " warn" : "");
  const bar = document.createElement("i");
  bar.style.width = `${Math.min(100, fill * 100)}%`;
  meter.append(bar);
  card.append(media, title, meta, meter);
  card.addEventListener("click", () => void open(item.id));
  return card;
}

async function reloadShelf(): Promise<void> {
  try {
    shelf = await call<TrackerSummary[]>(CHANNELS.trackerList);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not load your songs"));
    shelf = [];
  }
  byId("tk-shelf").replaceChildren(...shelf.map(shelfCard));
  byId("tk-empty").hidden = shelf.length > 0;
  byId("tk-count").textContent = shelf.length ? t("{n} songs", { n: shelf.length }) : "";
}

async function open(id: string): Promise<void> {
  try {
    const song = await call<TrackerSong>(CHANNELS.trackerGet, id);
    showEditor(song);
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("that song is gone"));
    void reloadShelf();
  }
}

async function create(): Promise<void> {
  try {
    showEditor(await call<TrackerSong>(CHANNELS.trackerCreate, ""));
  } catch (error) {
    flashToast(error instanceof Error ? error.message : t("could not make a song"));
  }
}

// a backup is a song saved as .json. it becomes a new song, so opening one never overwrites another
async function openBackup(file: File): Promise<void> {
  try {
    const raw = JSON.parse(await file.text()) as TrackerSong;
    if (!raw || !Array.isArray(raw.patterns) || !Array.isArray(raw.sounds)) throw new Error("not a song");
    const made = await call<TrackerSong>(CHANNELS.trackerCreate, String(raw.title || "").slice(0, 40));
    showEditor(await call<TrackerSong>(CHANNELS.trackerSave, { ...raw, id: made.id }));
  } catch {
    flashToast(t("that does not look like a tracker backup"));
  }
}

async function leave(): Promise<void> {
  stopSong();
  closeSheet();
  await flushSave();
  closeSong();
  editor().hidden = true;
  byId("tk-shelf-block").hidden = false;
  void reloadShelf();
}

// --- the editor

function showEditor(song: TrackerSong): void {
  tab = "notes";
  orderAt = 0;
  byId("tk-shelf-block").hidden = true;
  editor().hidden = false;
  loadSong(song);
  byId<HTMLInputElement>("tk-title").value = song.title;
  setTab("notes");
  say(t("opened {name}", { name: song.title }));
}

function setTab(next: Tab): void {
  tab = next;
  if (next !== "notes") closeSheet();
  for (const button of editor().querySelectorAll<HTMLElement>(".tk-tabs button")) button.setAttribute("aria-selected", String(button.dataset.tab === next));
  for (const name of TABS) byId(`tk-p-${name}`).hidden = name !== next;
  renderTab();
}

function renderTab(): void {
  if (!state.song) return;
  if (tab === "notes") renderNotes();
  else if (tab === "song") renderSongTab();
  else if (tab === "sounds") renderSounds();
  else renderExport();
}

// --- the limits bar

interface Meter {
  name: string;
  tip: string;
  value: string;
  max: string;
  unit: string;
  fill: number;
  state: "ok" | "warn" | "bad";
}

function renderLimits(): void {
  const song = state.song;
  if (!song) return;
  const report = analyse(song);
  const states = Object.fromEntries(report.checks.map((check) => [check.id, check.state]));
  const meters: Meter[] = [
    { name: t("voices"), tip: t("how many notes can play at the same moment. the SNES has 8"), value: String(report.voices), max: String(report.voiceMax), unit: "", fill: report.voices / report.voiceMax, state: states.voices as Meter["state"] },
    { name: t("sounds"), tip: t("how many different sounds the song uses. the SNES takes 64"), value: String(report.sounds), max: String(report.soundMax), unit: "", fill: report.sounds / report.soundMax, state: states.sounds as Meter["state"] },
    { name: t("memory"), tip: t("how much room the sounds take after SNES compression. you get 58 KB"), value: kb(report.bytes), max: kb(report.byteMax), unit: " KB", fill: report.bytes / report.byteMax, state: states.memory as Meter["state"] }
  ];
  const holder = byId("tk-limits");
  holder.replaceChildren(...meters.map((meter) => {
    const box = document.createElement("div");
    box.className = `tk-lim ${meter.state === "ok" ? "" : meter.state}`;
    box.title = meter.tip;
    const name = document.createElement("span");
    name.className = "name";
    name.textContent = meter.name;
    const value = document.createElement("span");
    value.className = "val";
    value.innerHTML = `${meter.value} <small>/ ${meter.max}${meter.unit}</small>`;
    const bar = document.createElement("span");
    bar.className = "tk-meter";
    const fill = document.createElement("b");
    fill.style.width = `${Math.min(100, Math.max(0, meter.fill * 100))}%`;
    bar.append(fill);
    box.append(name, value, bar);
    return box;
  }));
  const bad = report.checks.filter((check) => check.state === "bad");
  const fatal = bad.find((check) => check.fatal);
  const overSnes = report.checks.filter((check) => ["voices", "sounds", "memory"].includes(check.id) && check.state !== "ok");
  const line = byId("tk-say");
  line.className = "tk-say";
  if (report.blocked) {
    line.classList.add("bad");
    line.textContent = fatal ? `${t(fatal.detail)} ${t(fatal.fix)}` : `${t("Too much for the SNES.")} ${bad.map((check) => t(check.fix)).join(" ")} ${t("The game file is locked until this is fixed.")}`;
  } else if (song.mode === "free" && (report.voices > report.voiceMax || report.sounds > report.soundMax || report.bytes > report.byteMax)) {
    line.classList.add("warn");
    line.textContent = t("Over the SNES limits. Fine to play and export in Free mode, but it will not work on a real SNES.");
  } else if (overSnes.length) {
    line.classList.add("warn");
    line.textContent = overSnes.map((check) => t(check.fix)).filter(Boolean).join(" ") || t("Getting close to a limit.");
  } else line.textContent = t("Everything fits on a real SNES.");
  byId("tk-lcd-title").textContent = song.title;
  const hint = song.mode === "snes" ? t("locked to what a real SNES can play. use this for the game") : t("no locks. good for sketching, but the song may not fit on a real SNES");
  byId("tk-modehint").textContent = hint;
  for (const button of byId("tk-mode").querySelectorAll("button")) button.setAttribute("aria-pressed", String(button.dataset.mode === song.mode));
  byId<HTMLButtonElement>("tk-undo").disabled = !canUndo();
  byId<HTMLButtonElement>("tk-redo").disabled = !canRedo();
}

// --- the song tab

function chip(label: string, pressed: boolean, run: () => void, extra = ""): HTMLElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "tk-chip";
  button.setAttribute("aria-pressed", String(pressed));
  button.textContent = label;
  if (extra) button.title = extra;
  button.addEventListener("click", run);
  return button;
}

function renderSongTab(): void {
  const song = state.song;
  if (!song) return;
  orderAt = Math.min(orderAt, song.order.length - 1);
  byId("tk-patterns").replaceChildren(...song.patterns.map((pattern, index) => chip(`${index + 1}`, index === state.pat, () => {
    state.pat = index;
    state.sel = null;
    renderSongTab();
  }, t("{n} rows", { n: pattern.rows }))));
  byId("tk-order").replaceChildren(...song.order.map((pattern, index) => chip(`${index + 1}→${pattern + 1}`, index === orderAt, () => {
    orderAt = index;
    state.pat = pattern;
    renderSongTab();
  }, t("position {n} plays pattern {p}", { n: index + 1, p: pattern + 1 }))));
  const rows = byId<HTMLSelectElement>("tk-rows");
  rows.replaceChildren(...ROW_CHOICES.map((count) => new Option(t("{n} rows", { n: count }), String(count))));
  rows.value = String(song.patterns[state.pat].rows);
  byId<HTMLButtonElement>("tk-pat-del").disabled = song.patterns.length <= 1;
  byId<HTMLButtonElement>("tk-ord-del").disabled = song.order.length <= 1;
  byId<HTMLButtonElement>("tk-ord-left").disabled = orderAt <= 0;
  byId<HTMLButtonElement>("tk-ord-right").disabled = orderAt >= song.order.length - 1;
  byId<HTMLButtonElement>("tk-ord-prev").disabled = song.order[orderAt] <= 0;
  byId<HTMLButtonElement>("tk-ord-next").disabled = song.order[orderAt] >= song.patterns.length - 1;
  byId<HTMLInputElement>("tk-speed").value = String(song.speed);
  byId("tk-speed-v").textContent = String(song.speed);
  byId<HTMLInputElement>("tk-tempo").value = String(song.tempo);
  byId("tk-tempo-v").textContent = String(song.tempo);
  byId("tk-bpm").textContent = t("about {n} beats per minute. a lower speed number is faster", { n: Math.round((song.tempo * 6) / song.speed) });
  byId<HTMLInputElement>("tk-sfx").checked = song.sfx;
}

function bindSongTab(): void {
  byId("tk-pat-new").addEventListener("click", () => {
    if (state.song && state.song.patterns.length >= MAX_PATTERNS) return flashToast(t("that is the most patterns a song holds"));
    edit((song) => {
      song.patterns.push({ rows: song.patterns[state.pat].rows, notes: [] });
      state.pat = song.patterns.length - 1;
    }, "song");
  });
  byId("tk-pat-copy").addEventListener("click", () => {
    if (state.song && state.song.patterns.length >= MAX_PATTERNS) return flashToast(t("that is the most patterns a song holds"));
    edit((song) => {
      const source = song.patterns[state.pat];
      song.patterns.push({ rows: source.rows, notes: source.notes.map((note) => ({ ...note })) });
      state.pat = song.patterns.length - 1;
    }, "song");
  });
  byId("tk-pat-clear").addEventListener("click", () => edit((song) => void (song.patterns[state.pat].notes = []), "song"));
  byId("tk-pat-del").addEventListener("click", () => {
    const removed = state.pat;
    edit((song) => {
      if (song.patterns.length <= 1) return;
      song.patterns.splice(removed, 1);
      song.order = song.order.filter((value) => value !== removed).map((value) => (value > removed ? value - 1 : value));
      if (!song.order.length) song.order.push(0);
      state.pat = Math.min(removed, song.patterns.length - 1);
      orderAt = Math.min(orderAt, song.order.length - 1);
    }, "song");
  });
  byId<HTMLSelectElement>("tk-rows").addEventListener("change", (event) => {
    const rows = Number((event.target as HTMLSelectElement).value);
    edit((song) => {
      const pattern = song.patterns[state.pat];
      pattern.rows = rows;
      pattern.notes = pattern.notes.filter((note) => note.r < rows);
    }, "song");
  });
  byId("tk-ord-add").addEventListener("click", () => edit((song) => {
    song.order.push(state.pat);
    orderAt = song.order.length - 1;
  }, "song"));
  byId("tk-ord-prev").addEventListener("click", () => edit((song) => {
    song.order[orderAt] = Math.max(0, song.order[orderAt] - 1);
    state.pat = song.order[orderAt];
  }, "song"));
  byId("tk-ord-next").addEventListener("click", () => edit((song) => {
    song.order[orderAt] = Math.min(song.patterns.length - 1, song.order[orderAt] + 1);
    state.pat = song.order[orderAt];
  }, "song"));
  byId("tk-ord-left").addEventListener("click", () => edit((song) => {
    if (orderAt <= 0) return;
    [song.order[orderAt - 1], song.order[orderAt]] = [song.order[orderAt], song.order[orderAt - 1]];
    orderAt--;
  }, "song"));
  byId("tk-ord-right").addEventListener("click", () => edit((song) => {
    if (orderAt >= song.order.length - 1) return;
    [song.order[orderAt + 1], song.order[orderAt]] = [song.order[orderAt], song.order[orderAt + 1]];
    orderAt++;
  }, "song"));
  byId("tk-ord-del").addEventListener("click", () => edit((song) => {
    if (song.order.length <= 1) return;
    song.order.splice(orderAt, 1);
    orderAt = Math.min(orderAt, song.order.length - 1);
  }, "song"));
  byId<HTMLInputElement>("tk-speed").addEventListener("input", (event) => {
    const value = Number((event.target as HTMLInputElement).value);
    edit((song) => void (song.speed = value), "quiet", "speed");
    renderSongTab();
  });
  byId<HTMLInputElement>("tk-tempo").addEventListener("input", (event) => {
    const value = Number((event.target as HTMLInputElement).value);
    edit((song) => void (song.tempo = value), "quiet", "tempo");
    renderSongTab();
  });
  byId<HTMLInputElement>("tk-sfx").addEventListener("change", (event) => {
    const on = (event.target as HTMLInputElement).checked;
    edit((song) => void (song.sfx = on), "all");
  });
  byId("tk-del").addEventListener("click", () => {
    const song = state.song;
    if (!song || !confirm(t("delete {name}? this cannot be undone", { name: song.title }))) return;
    const id = song.id;
    stopSong();
    closeSong();
    editor().hidden = true;
    byId("tk-shelf-block").hidden = false;
    void call(CHANNELS.trackerRemove, id).then(reloadShelf);
  });
}

// --- the mode switch

function setMode(mode: TrackerMode): void {
  const song = state.song;
  if (!song || song.mode === mode) return;
  if (mode === "snes") {
    const dropped = song.patterns.reduce((sum, pattern) => sum + pattern.notes.filter((note) => note.c >= LIMITS.snes.voices).length, 0);
    if (dropped && !confirm(t("SNES safe mode has 8 voices. {n} notes on the extra voices will be removed. you can undo this. continue?", { n: dropped }))) return;
  }
  edit((draft) => {
    draft.mode = mode;
    if (mode === "snes") Object.assign(draft, clampToMode(draft));
  }, "all");
  say(mode === "snes" ? t("SNES safe mode on") : t("Free mode on"));
}

// --- playing

function stepText(step: Step | null): string {
  return step ? t("PATTERN {p} · ROW {r}", { p: step.pattern + 1, r: String(step.row).padStart(2, "0") }) : t("READY");
}

function setPlaying(on: boolean): void {
  const play = byId("tk-play");
  play.innerHTML = iconSvg(on ? "pause" : "play", 26);
  play.setAttribute("aria-label", on ? t("stop") : t("play the song"));
  byId("tk-pat").setAttribute("aria-pressed", "false");
}

function start(only: boolean): void {
  const song = state.song;
  if (!song) return;
  if (isPlaying()) {
    stopSong();
    return;
  }
  const loop = byId("tk-loop").getAttribute("aria-pressed") === "true";
  const target: TrackerSong = only ? { ...song, order: [state.pat] } : song;
  setPlaying(true);
  if (only) byId("tk-pat").setAttribute("aria-pressed", "true");
  playSong(target, {
    loop,
    snes: earOn(),
    onStep: (step) => {
      byId("tk-lcd-pos").textContent = stepText(step);
      showStep(step);
    },
    onEnd: () => setPlaying(false)
  });
}

// --- boot

export function setupTrackerView(): void {
  setupNotes({ ear: earOn });
  setupSounds();
  bindSongTab();

  onSong((scope: Scope) => {
    if (!state.song || editor().hidden) return;
    renderLimits();
    const title = byId<HTMLInputElement>("tk-title");
    if (document.activeElement !== title) title.value = state.song.title;
    const visible = scope === "all" || (scope === "notes" && tab === "notes") || (scope === "song" && (tab === "song" || tab === "notes")) || (scope === "sounds" && tab === "sounds");
    if (visible) renderTab();
    else if (tab === "export") renderExport();
  });

  byId("tk-new").addEventListener("click", () => void create());
  byId<HTMLInputElement>("tk-open").addEventListener("change", (event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (file) void openBackup(file);
  });
  byId("tk-back").addEventListener("click", () => void leave());
  byId<HTMLInputElement>("tk-title").addEventListener("input", (event) => {
    const value = (event.target as HTMLInputElement).value.slice(0, 40);
    edit((song) => void (song.title = value || "new song"), "quiet", "title");
  });
  for (const button of byId("tk-mode").querySelectorAll<HTMLElement>("button")) button.addEventListener("click", () => setMode(button.dataset.mode as TrackerMode));
  for (const button of editor().querySelectorAll<HTMLElement>(".tk-tabs button")) button.addEventListener("click", () => setTab(button.dataset.tab as Tab));
  byId("tk-play").addEventListener("click", () => start(false));
  byId("tk-pat").addEventListener("click", () => start(true));
  byId("tk-loop").addEventListener("click", () => {
    const loop = byId("tk-loop");
    loop.setAttribute("aria-pressed", String(loop.getAttribute("aria-pressed") !== "true"));
  });
  byId("tk-undo").addEventListener("click", undo);
  byId("tk-redo").addEventListener("click", redo);
  byId("tk-play").innerHTML = iconSvg("play", 26);

  window.addEventListener("keydown", (event) => {
    if (!isViewActive("tracker") || editor().hidden || !state.song) return;
    const typing = (event.target as HTMLElement | null)?.closest("input, textarea, select");
    if ((event.ctrlKey || event.metaKey) && !typing) {
      const key = event.key.toLowerCase();
      if (key === "z") {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
      } else if (key === "y") {
        event.preventDefault();
        redo();
      }
      return;
    }
    // space plays unless it is pressing a button. squares and tabs are buttons only because they can be tapped
    const pressing = (event.target as HTMLElement | null)?.closest("button:not(.tk-cell, [role=tab])");
    if (event.key === " " && !typing && !pressing) {
      event.preventDefault();
      start(false);
      return;
    }
    if (tab === "notes" && handleKey(event)) event.preventDefault();
  });

  listen(CHANNELS.trackerUpdated, () => {
    if (editor().hidden) void reloadShelf();
  });
  onViewShown("tracker", () => {
    if (editor().hidden) void reloadShelf();
  });
  onViewHidden("tracker", () => {
    stopSong();
    void flushSave();
  });
}
